import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SearchIcon from '@mui/icons-material/Search';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export const GRID_PREFERENCES_STORAGE_KEY = 'jobsGridPreferences';
export const DEFAULT_GRID_PREFERENCE = { columnOrder: [], columnVisibilityModel: {} };

export const parseGridPreferences = () => {
  const stored = window.localStorage.getItem(GRID_PREFERENCES_STORAGE_KEY);
  if (!stored) {
    return {};
  }

  try {
    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export const sanitizeGridPreference = (preference, validFields) => {
  const validFieldSet = new Set(validFields);
  const incomingOrder = Array.isArray(preference?.columnOrder) ? preference.columnOrder : [];

  const columnOrder = incomingOrder.filter(
    (field, index) => typeof field === 'string' && validFieldSet.has(field) && incomingOrder.indexOf(field) === index,
  );

  validFields.forEach((field) => {
    if (!columnOrder.includes(field)) {
      const previousKnownField = [...validFields]
        .slice(0, validFields.indexOf(field))
        .reverse()
        .find((candidate) => columnOrder.includes(candidate));

      if (previousKnownField) {
        const insertAt = columnOrder.indexOf(previousKnownField) + 1;
        columnOrder.splice(insertAt, 0, field);
        return;
      }

      const nextKnownField = validFields
        .slice(validFields.indexOf(field) + 1)
        .find((candidate) => columnOrder.includes(candidate));

      if (nextKnownField) {
        const insertAt = columnOrder.indexOf(nextKnownField);
        columnOrder.splice(insertAt, 0, field);
        return;
      }

      columnOrder.push(field);
    }
  });

  const sourceModel = preference?.columnVisibilityModel && typeof preference.columnVisibilityModel === 'object'
    ? preference.columnVisibilityModel
    : {};

  const columnVisibilityModel = Object.entries(sourceModel).reduce((acc, [field, value]) => {
    if (validFieldSet.has(field) && typeof value === 'boolean') {
      acc[field] = value;
    }
    return acc;
  }, {});

  return { columnOrder, columnVisibilityModel };
};

export const applyColumnOrder = (baseColumns, orderedFields) => {
  const columnsByField = new Map(baseColumns.map((column) => [column.field, column]));
  const orderedColumns = [];

  orderedFields.forEach((field) => {
    const column = columnsByField.get(field);
    if (column) {
      orderedColumns.push(column);
      columnsByField.delete(field);
    }
  });

  return [...orderedColumns, ...columnsByField.values()];
};

function SortableColumnRow({
  field,
  isDarkMode,
  isVisible,
  label,
  onToggleColumnVisibility,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.5,
        py: 0.25,
        borderRadius: 1,
        backgroundColor: isDragging ? (isDarkMode ? '#1e293b' : '#e2e8f0') : 'transparent',
        '&:hover': {
          backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5',
        },
      }}
    >
      <Checkbox
        size="small"
        checked={isVisible}
        onChange={() => onToggleColumnVisibility(field)}
      />
      <Typography
        sx={{
          flex: 1,
          fontSize: '0.8125rem',
          color: isVisible ? 'text.primary' : 'text.secondary',
          textDecoration: isVisible ? 'none' : 'line-through',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={label}
      >
        {label}
      </Typography>
      <IconButton
        size="small"
        aria-label={`Drag ${label}`}
        sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon fontSize="inherit" />
      </IconButton>
    </Box>
  );
}

export function JobsColumnsPopover({
  open,
  anchorEl,
  onClose,
  isDarkMode,
  activeGridPreference,
  columnsByField,
  onToggleColumnVisibility,
  onReorderColumns,
  onResetColumns,
}) {
  const [searchQuery, setSearchQuery] = React.useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 4,
      },
    }),
  );

  React.useEffect(() => {
    if (!open) {
      setSearchQuery('');
    }
  }, [open]);

  const filteredColumnOrder = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const columnOrder = Array.isArray(activeGridPreference?.columnOrder)
      ? activeGridPreference.columnOrder
      : [];

    if (!query) {
      return columnOrder;
    }

    return columnOrder.filter((field) => {
      const column = columnsByField.get(field);
      if (!column) return false;
      const label = (column.headerName || field).toLowerCase();
      return label.includes(query);
    });
  }, [activeGridPreference?.columnOrder, columnsByField, searchQuery]);

  const handleDragEnd = React.useCallback((event) => {
    const { active, over } = event;
    if (!active?.id || !over?.id || active.id === over.id) {
      return;
    }

    onReorderColumns(active.id, over.id);
  }, [onReorderColumns]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: {
          width: 320,
          maxHeight: 520,
          mt: 1,
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ px: 2, py: 1.5, borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Columns</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1.25 }}>
          Toggle visibility and reorder columns
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Search columns..."
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          inputProps={{
            'aria-label': 'Search columns',
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '0.8125rem',
              backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc',
            },
          }}
        />
      </Box>

      <Box sx={{ maxHeight: 380, overflow: 'auto', px: 1, py: 1 }}>
        {filteredColumnOrder.length === 0 ? (
          <Typography
            sx={{
              px: 1,
              py: 2,
              fontSize: '0.8125rem',
              color: 'text.secondary',
              textAlign: 'center',
            }}
          >
            No columns found
          </Typography>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filteredColumnOrder}
              strategy={verticalListSortingStrategy}
            >
              {filteredColumnOrder.map((field) => {
                const column = columnsByField.get(field);
                if (!column) return null;

                const isVisible = activeGridPreference.columnVisibilityModel[field] !== false;

                return (
                  <SortableColumnRow
                    key={field}
                    field={field}
                    isDarkMode={isDarkMode}
                    isVisible={isVisible}
                    label={column.headerName || field}
                    onToggleColumnVisibility={onToggleColumnVisibility}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        )}
      </Box>

      <Divider />
      <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Button size="small" onClick={onResetColumns}>
          Reset
        </Button>
        <Button size="small" variant="contained" onClick={onClose}>
          Done
        </Button>
      </Box>
    </Popover>
  );
}
